// lib/koi-pond-v3/interaction/surfaceBreak.ts
//
// T11.5 + T11.6 — the signature "break the moon's reflection" moment.
//
// Owner steer (Phase 10 close-out): model the break as a LOCAL water-normal
// / reflection disturbance where the koi pushes up against the surface — NOT
// a literal above-water breach mesh. A global flicker of the whole reflection
// band would read as a lighting bug; a local break reads as the koi. So this
// controller doesn't render anything itself — it watches the koi and, at the
// right moment, fires a small CLUSTER of staggered ripples through the
// existing water-disturbance pool (the same uDisturb* path clicks + wake use).
// Those ripples shatter the moon reflection locally and reform over ~2.5s as
// they dissipate. A break also `startle`s nearby koi, same as a click.
//
//   T11.5 — AMBIENT break: when a koi rises into a surface excursion AND its
//           position sits in the moon's reflection lane, it breaks the
//           reflection. Biased toward page-just-loaded and visitor-idle so the
//           first thing a lingering visitor notices is the pond's signature
//           beat — not a constant stream of breaks.
//
//   T11.6 — SCROLL break: as the visitor scrolls past the hero, a designated
//           koi is urged to the surface and breaks the reflection at the
//           moment it leaves the hero — tying the scene to page navigation.
//           Explicit interaction, so it bypasses the ambient lane/idle gates.
//
// The "urge a koi to surface" capability lives on the swimmer (koiPathFollowing
// `urgeSurface`); this controller only decides WHEN to call it and WHEN the
// resulting surfacing should fire a break.

import { Vector2 } from 'three';

export interface SurfaceBreakKoi {
  /** Live planar/vertical position of the koi (read each frame). */
  pos: { x: number; y: number; z: number };
  /** Push this koi toward the surface for a short window (T11.6). */
  urgeSurface: (t: number, durationSec?: number) => void;
}

export interface SurfaceBreakOptions {
  /** Fire one ripple through the shared water-disturbance pool. */
  add: (x: number, z: number, strength: number, startle: boolean, size: number) => void;
  /** Moon's XZ projection on the water plane (for the reflection-lane test). */
  moonXZ: Vector2;
  /** Viewer's XZ projection — getter because the camera can move (OrbitControls). */
  getViewerXZ: () => Vector2;
  /** TEMP review aid: compress the ambient-break cadence so a reviewer can
   *  watch several fire in a short sitting. 1 = production cadence. */
  fastCadence?: boolean;
}

// y at the very top of a koi's rise excursion is ~0.45 (koiPathFollowing
// clamps to 0.45; water surface sits at y=1). "Near the surface" = the koi is
// up in the top of that band, pushing against the underside of the surface.
const NEAR_SURFACE_Y = 0.28;
// Re-arm only once the koi has clearly dropped back to its depth band, so one
// rise = at most one break (no machine-gunning while it lingers near the top).
const REARM_Y = -0.15;
// Same lateral falloff the water shader uses for the moon-trail spine, so
// "in the lane" here matches where the reflection actually is on screen.
const SPINE_FALLOFF = 0.3;
// A koi counts as "in the reflection lane" when its spine closeness clears this.
const LANE_GATE = 0.5;

export function makeSurfaceBreak(o: SurfaceBreakOptions) {
  const moonXZ = o.moonXZ;
  const fast = o.fastCadence ?? false;

  // Global cooldown between ambient breaks so they stay a rare, noticed beat.
  const AMBIENT_COOLDOWN = fast ? 6 : 22;
  // How long after load we treat as "page just loaded" (breaks come easy).
  const FRESH_LOAD_SEC = 25;
  // Idle = no pointer activity for this long → bias breaks up.
  const IDLE_SEC = 30;

  // Per-koi rise tracking (armed = eligible to break on this rise).
  const tracks: Array<{ lastY: number; armed: boolean }> = [];
  let lastAmbientT = -1e9;

  // Staggered "shatter" rings queued after a primary break, for the
  // fragmenting-then-reforming feel (one strong ring + two softer trailing).
  const queued: Array<{ at: number; x: number; z: number; strength: number; size: number }> = [];

  // One-shot scroll break: when armed, the NEXT time the designated koi nears
  // the surface (or a timeout elapses) we fire a break regardless of the lane.
  let scrollWatch: { koi: number; deadline: number } | null = null;

  // scratch
  const _dir = new Vector2();
  const _off = new Vector2();

  /** Closeness (0..1) of an XZ point to the moon→viewer reflection spine. */
  function laneCloseness(x: number, z: number): number {
    const viewer = o.getViewerXZ();
    _dir.copy(viewer).sub(moonXZ);
    if (_dir.lengthSq() < 1e-6) return 0;
    _dir.normalize();
    _off.set(x - moonXZ.x, z - moonXZ.y);
    const along = _off.dot(_dir);
    // perpendicular distance from the spine line
    const px = _off.x - _dir.x * along;
    const pz = _off.y - _dir.y * along;
    const perpSq = px * px + pz * pz;
    return Math.exp(-perpSq * SPINE_FALLOFF);
  }

  /** Fire a clustered break at (x,z): one strong ring now + two staggered. */
  function shatter(t: number, x: number, z: number) {
    o.add(x, z, 1.5, true, 1.35); // primary — strong, startles koi, large ring
    queued.push({ at: t + 0.16, x, z, strength: 1.0, size: 0.95 });
    queued.push({ at: t + 0.36, x: x + 0.25, z: z - 0.2, strength: 0.7, size: 0.7 });
  }

  /**
   * T11.6 — call when the visitor scrolls past the hero. Picks a designated
   * koi (the one currently nearest the surface, so the rise reads as natural),
   * urges it up, and arms a one-shot break that fires when it surfaces.
   */
  function triggerScrollBreak(t: number, koi: SurfaceBreakKoi[]) {
    if (scrollWatch) return; // already in flight — ignore repeat scroll crossings
    if (koi.length === 0) return;
    let pick = 0;
    let best = -Infinity;
    for (let i = 0; i < koi.length; i++) {
      if (koi[i].pos.y > best) {
        best = koi[i].pos.y;
        pick = i;
      }
    }
    koi[pick].urgeSurface(t, 3.0);
    scrollWatch = { koi: pick, deadline: t + 3.2 };
  }

  function update(t: number, koi: SurfaceBreakKoi[], ctx: { idle: boolean; sinceLoad: number }) {
    // Flush any queued shatter rings whose time has come.
    for (let i = queued.length - 1; i >= 0; i--) {
      if (t >= queued[i].at) {
        const q = queued[i];
        o.add(q.x, q.z, q.strength, false, q.size);
        queued.splice(i, 1);
      }
    }

    // Keep per-koi tracks sized to the school.
    while (tracks.length < koi.length) tracks.push({ lastY: koi[tracks.length].pos.y, armed: false });

    // T11.6 — resolve an in-flight scroll break.
    if (scrollWatch) {
      const k = koi[scrollWatch.koi];
      if (k && k.pos.y > NEAR_SURFACE_Y) {
        shatter(t, k.pos.x, k.pos.z);
        scrollWatch = null;
      } else if (t > scrollWatch.deadline) {
        // Koi didn't quite breach in the window — fire at its current spot so
        // the scroll gesture still pays off rather than silently no-op'ing.
        if (k) shatter(t, k.pos.x, k.pos.z);
        scrollWatch = null;
      }
    }

    // T11.5 — ambient breaks.
    const cooled = t - lastAmbientT >= AMBIENT_COOLDOWN;
    for (let i = 0; i < koi.length; i++) {
      const tr = tracks[i];
      const y = koi[i].pos.y;
      const rising = y > tr.lastY;
      tr.lastY = y;

      if (y < REARM_Y) tr.armed = true; // back at depth → eligible again

      if (!cooled || !tr.armed) continue;
      if (!rising || y < NEAR_SURFACE_Y) continue;

      const lane = laneCloseness(koi[i].pos.x, koi[i].pos.z);
      if (lane < LANE_GATE) continue;

      // Bias: page-just-loaded or idle visitors get the beat readily; otherwise
      // require the koi to be well inside the bright lane so breaks stay rare.
      const eager = ctx.sinceLoad < FRESH_LOAD_SEC || ctx.idle;
      if (!eager && lane < 0.78) continue;

      shatter(t, koi[i].pos.x, koi[i].pos.z);
      tr.armed = false;
      lastAmbientT = t;
      break; // at most one ambient break per frame
    }
  }

  return { update, triggerScrollBreak };
}
